import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "@apollo/client/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddOfferForm from "../components/AddOfferForm";
import AllOfferTable from "../components/AllOfferTable";
import themestyle from "../theme";
import ConfirmDialog from "../components/confirmdialogbox";
import CustomSnackbar from "../components/Snackbar";
import { DELETE_OFFER } from "../graphql/mutation";
import type { DeleteOfferMutationResponse } from "../interfaces/offer.interface";

type ViewType = "ALL_OFFERS" | "ADD_OFFER" | "VIEW_OFFER" | "EDIT_OFFER";

const OfferManagement: React.FC = () => {
  const navigate = useNavigate();
  const { mode, id } = useParams<{ mode?: string; id?: string }>();
  const location = useLocation();

  const [refreshCount, setRefreshCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<{ id: string; name: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");

  const [deleteOffer] = useMutation<DeleteOfferMutationResponse, { id: string }>(DELETE_OFFER);

  const getActiveView = (): ViewType => {
    if (location.pathname === "/offers/add") return "ADD_OFFER";
    if (mode === "view" && id) return "VIEW_OFFER";
    if (mode === "edit" && id) return "EDIT_OFFER";
    return "ALL_OFFERS";
  };

  const activeView = getActiveView();
  const selectedOfferId = id;

  useEffect(() => {
    document.title = "Offers - United Grooves";
  }, []);

  const handleView = (offerId: string) => {
    navigate(`/offers/view/${offerId}`, { state: { from: location.pathname } });
  };

  const handleEdit = (offerId?: string) => {
    const targetId = offerId || selectedOfferId;
    if (targetId) {
      navigate(`/offers/edit/${targetId}`, { state: { from: location.pathname } });
    }
  };

  const handleBack = () => {
    const from = (location.state as any)?.from;
    navigate(from || "/offers");
  };

  const handleSuccess = () => {
    navigate("/offers");
    setRefreshCount((prev) => prev + 1);
  };

  const handleDeleteClick = (offerId: string, name: string) => {
    setOfferToDelete({ id: offerId, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!offerToDelete) return;

    try {
      const { data } = await deleteOffer({ variables: { id: offerToDelete.id } });
      if (data?.deleteOffer?.success) {
        setSnackbarMessage(data.deleteOffer.message || "Offer deleted successfully");
        setSnackbarType("error");
        setSnackbarOpen(true);
        setDeleteDialogOpen(false);
        setOfferToDelete(null);
        handleSuccess();
      } else {
        setSnackbarMessage(data?.deleteOffer?.message || "Failed to delete offer");
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteDialogOpen(false);
      setOfferToDelete(null);
    }
  };

  const isAllOffersActive = activeView === "ALL_OFFERS";

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          {(activeView === "VIEW_OFFER" || activeView === "EDIT_OFFER") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              Back
            </Box>
          )}

          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: "24px" }}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            {activeView === "VIEW_OFFER"
              ? "View Offer"
              : activeView === "EDIT_OFFER"
                ? "Edit Offer"
                : "Offers"}
          </Typography>
        </Box>

        {(activeView === "ALL_OFFERS" || activeView === "ADD_OFFER") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => navigate("/offers")}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllOffersActive ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllOffersActive ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              All Offers
            </Box>
            <Box
              onClick={() => navigate("/offers/add")}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: activeView === "ADD_OFFER" ? "#4CAF50" : "#6B7280",
                backgroundColor: activeView === "ADD_OFFER" ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Add Offer
            </Box>
          </Box>
        )}
      </Box>

      {activeView === "ALL_OFFERS" && (
        <AllOfferTable
          refreshTrigger={refreshCount}
          onView={handleView}
          onDelete={handleDeleteClick}
        />
      )}

      {activeView === "ADD_OFFER" && <AddOfferForm mode="add" onSuccess={handleSuccess} onCancel={handleBack} />}

      {activeView === "VIEW_OFFER" && (
        <AddOfferForm
          mode="view"
          id={selectedOfferId}
          onCancel={handleBack}
          onEdit={() => handleEdit(selectedOfferId)}
        />
      )}

      {activeView === "EDIT_OFFER" && (
        <AddOfferForm
          mode="edit"
          id={selectedOfferId}
          onSuccess={handleSuccess}
          onCancel={() => navigate(`/offers/view/${selectedOfferId}`)}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setOfferToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete ${offerToDelete?.name || "this offer"}?`}
        icon={<DeleteOutlineIcon sx={{ color: "#EB2857", fontSize: 36 }} />}
        iconBgColor="danger"
        confirmButtonColor="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default OfferManagement;
